import { useLocation, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { Card, Form, Input, Select, Button, message } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { userAPI } from "../../services/api";

const { Option } = Select;

const CreateUser = () => {
  const [form] = Form.useForm();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await userAPI.createUserByAdmin({ ...values, ...(state?.labId ? { role: "labadmin", labId: state.labId } : {}) });

      message.success(
        "User created successfully."
      );
      form.resetFields();
      if (state?.labId) navigate("/superadmin/labs");

    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create user. Please try again.";
      message.error(errorMessage);
      console.error("Error creating user:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={state?.labId ? `Create administrator for ${state.labName}` : "Create User"}
      style={{
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        maxWidth: 600,
        margin: "0 auto",
      }}
    >
      <Form
        form={form}
        initialValues={{ role: state?.labId ? "labadmin" : pathname.includes("doctors") ? "doctor" : "patient" }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        requiredMark="optional"
      >
        {/* Name Field */}
        <Form.Item
          label="Full Name"
          name="name"
          rules={[
            { required: true, message: "Please enter full name" },
            { min: 2, message: "Name must be at least 2 characters" },
            { max: 50, message: "Name cannot exceed 50 characters" },
          ]}
        >
          <Input
            prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Enter full name"
            size="large"
          />
        </Form.Item>

        {/* Email Field */}
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please enter email address" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Enter email address"
            size="large"
          />
        </Form.Item>

        {/* Password Field */}
        <Form.Item
          label="Password"
          name="password"
          rules={[
            { required: true, message: "Please enter password" },
            { min: 8, message: "Password must be at least 8 characters" },
          ]}
          extra="Minimum 8 characters required"
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Enter password"
            size="large"
          />
        </Form.Item>

        {/* Mobile Field */}
        <Form.Item
          label="Mobile Number"
          name="phone"
          rules={[
            { required: true, message: "Please enter mobile number" },
            {
              pattern: /^[6-9]\d{9}$/,
              message: "Please enter a valid 10-digit mobile number",
            },
          ]}
        >
          <Input
            prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Enter 10-digit mobile number"
            size="large"
            maxLength={10}
          />
        </Form.Item>

        {/* Location Field (Optional) */}
        <Form.Item label="Location" name="location">
          <Input
            prefix={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Enter location (optional)"
            size="large"
          />
        </Form.Item>

        {/* Role Dropdown */}
        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: "Please select a role" }]}
        >
          <Select
            placeholder="Select user role"
            size="large"
            style={{ width: "100%" }}
          >
            <Option value="patient">Patient</Option>
            <Option value="labadmin">Lab Admin</Option>
            <Option value="doctor">Doctor</Option>
          </Select>
        </Form.Item>

        {/* Submit Button */}
        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            block
            style={{
              height: 45,
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            Create User
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CreateUser;
